import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { BarChart3, Search, Calendar, ShoppingCart, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { toast } from "sonner";

// Compute the [start, end) date range for the currently-active trip of the
// logged-in user's shift. Used as default range for product usage queries.
function computeCurrentTripRange(activeBoat, shift) {
  if (!activeBoat) return null;
  const cfg = (shift === "1") ? (activeBoat.shift1 || {}) : (activeBoat.shift2 || {});
  if (!cfg.start_date) return null;
  const cycleDays = cfg.cycle_days || 21;
  const firstTrip = cfg.first_trip_number || 1;
  const base = new Date(cfg.start_date); base.setHours(0,0,0,0);
  const today = new Date(); today.setHours(0,0,0,0);
  const diffDays = Math.floor((today - base) / 86400000);
  if (diffDays < 0) return null;
  const currentTrip = firstTrip + Math.floor(diffDays / (cycleDays * 2)) * 2;
  const tripOffset = Math.floor((currentTrip - firstTrip) / 2);
  const start = new Date(base);
  start.setDate(start.getDate() + tripOffset * cycleDays * 2);
  const end = new Date(start);
  end.setDate(end.getDate() + cycleDays);
  return { start, end, tripNumber: currentTrip };
}

const toISO = (d) => d.toISOString().slice(0, 10);

// Suggested quick-search products (translated to Norwegian)
const QUICK_SUGGESTIONS = ["mjølk", "bacon", "banan", "egg", "smør", "kaffe", "kylling", "laks", "yoghurt", "ost"];

function TrendCard({ label, count, price, range, highlight, testid }) {
  return (
    <div className={`rounded-lg p-3 ${highlight ? "bg-emerald-50 border border-emerald-300" : "bg-stone-50 border border-stone-200"}`} data-testid={testid}>
      <p className={`text-[10px] uppercase tracking-wider font-semibold ${highlight ? "text-emerald-700" : "text-stone-500"}`}>{label}</p>
      <p className="text-2xl font-bold text-stone-900 leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        {count} <span className="text-xs font-normal text-stone-500">kjøp</span>
      </p>
      <p className="text-sm text-stone-600">{price.toLocaleString("nb-NO")} kr</p>
      <p className="text-[10px] text-stone-400 mt-1">{range}</p>
    </div>
  );
}

function PctChange({ label, pct, prevVal }) {
  if (pct === null || pct === undefined) {
    return (
      <p className="text-xs text-stone-500" data-testid={`pct-${label.replace(/\s+/g, "-")}`}>
        Ingen data for forrige periode på {label}.
      </p>
    );
  }
  const isUp = pct > 1;
  const isDown = pct < -1;
  const Icon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
  const color = isUp ? "text-red-600" : isDown ? "text-emerald-700" : "text-stone-500";
  const sign = pct > 0 ? "+" : "";
  const phrase = isUp ? "meir" : isDown ? "mindre" : "uendret";
  return (
    <p className={`text-xs flex items-center gap-1.5 ${color} font-medium`} data-testid={`pct-${label.replace(/\s+/g, "-")}`}>
      <Icon className="h-3 w-3" />
      <span><strong>{sign}{pct}%</strong> {phrase} {label} enn forrige periode</span>
      <span className="text-stone-400 font-normal hidden sm:inline">(var {prevVal})</span>
    </p>
  );
}

export default function StatistikkPage() {
  const { user } = useAuth();
  const [activeBoat, setActiveBoat] = useState(null);
  const [shift, setShift] = useState("1");

  // Date-range filter (default = current trip if available)
  const [rangePreset, setRangePreset] = useState("trip"); // "trip" | "all" | "custom"
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Search query
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getActiveBoat().catch(() => ({ data: null }));
        if (!cancelled) {
          setActiveBoat(res.data);
          setShift(user?.preferred_shift || "1");
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [user?.preferred_shift]);

  const tripRange = computeCurrentTripRange(activeBoat, shift);

  // Sync date inputs with selected preset
  useEffect(() => {
    if (rangePreset === "trip" && tripRange) {
      setStartDate(toISO(tripRange.start));
      // -1 day so end is inclusive (turen slutter på sluttdag)
      const endInclusive = new Date(tripRange.end); endInclusive.setDate(endInclusive.getDate() - 1);
      setEndDate(toISO(endInclusive));
    } else if (rangePreset === "all") {
      setStartDate("");
      setEndDate("");
    }
    // "custom" — let user keep what they typed
  }, [rangePreset, activeBoat, shift, tripRange]);

  const search = useCallback(async (query) => {
    const term = (query || q).trim();
    if (!term) {
      toast.error("Skriv inn vare-namn (t.d. mjølk, bacon, banan)");
      return;
    }
    setLoading(true);
    setQ(term);
    try {
      // Compare previous only when we have a defined range AND it's NOT "all"
      const compare = !!(startDate && endDate);
      const res = await api.getProductUsage(term, startDate || null, endDate || null, compare);
      setResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Klarte ikkje hente statistikk");
    } finally {
      setLoading(false);
    }
  }, [q, startDate, endDate]);

  return (
    <div className="p-3 sm:p-6 max-w-4xl mx-auto" data-testid="statistikk-page">
      <header className="mb-6">
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-stone-900 flex items-center gap-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          <BarChart3 className="h-8 w-8 text-emerald-700" />
          Vareforbruk
        </h1>
        <p className="text-sm text-stone-500 mt-1">
          Sjå kor mykje du brukar av eit produkt på ein tur. Søk t.d. &quot;mjølk&quot;, &quot;bacon&quot;, &quot;banan&quot; osv.
        </p>
      </header>

      {/* Search + range controls */}
      <div className="bg-white border border-stone-200 rounded-xl p-4 mb-4 space-y-3" data-testid="search-controls">
        {/* Søkefelt */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") search(); }}
              placeholder="Skriv vare (t.d. mjølk, bacon, banan)…"
              className="w-full rounded-md border border-stone-200 bg-white pl-9 pr-3 py-2.5 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
              data-testid="product-search-input"
              autoFocus
            />
          </div>
          <button
            onClick={() => search()}
            disabled={loading || !q.trim()}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            data-testid="product-search-btn"
          >
            {loading ? "Søker…" : "Søk"}
          </button>
        </div>

        {/* Quick suggestions */}
        <div className="flex flex-wrap gap-1.5">
          {QUICK_SUGGESTIONS.map((s) => (
            <button
              key={s}
              onClick={() => search(s)}
              className="text-[11px] px-2 py-0.5 bg-stone-100 hover:bg-emerald-50 hover:text-emerald-800 rounded-full text-stone-700 transition-colors"
              data-testid={`quick-suggestion-${s}`}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Range preset */}
        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-stone-100">
          <Calendar className="h-4 w-4 text-stone-400" />
          <div className="inline-flex rounded-md border border-stone-200 overflow-hidden text-[11px] font-semibold" data-testid="range-preset">
            <button onClick={() => setRangePreset("trip")} className={`px-3 py-1.5 ${rangePreset === "trip" ? "bg-navy-800 text-white" : "bg-white text-stone-600 hover:bg-stone-50"}`} data-testid="range-trip">
              Denne turen{tripRange ? ` (tur ${tripRange.tripNumber})` : ""}
            </button>
            <button onClick={() => setRangePreset("all")} className={`px-3 py-1.5 border-l border-stone-200 ${rangePreset === "all" ? "bg-navy-800 text-white" : "bg-white text-stone-600 hover:bg-stone-50"}`} data-testid="range-all">
              Alle kvitteringar
            </button>
            <button onClick={() => setRangePreset("custom")} className={`px-3 py-1.5 border-l border-stone-200 ${rangePreset === "custom" ? "bg-navy-800 text-white" : "bg-white text-stone-600 hover:bg-stone-50"}`} data-testid="range-custom">
              Eigne datoar
            </button>
          </div>
          {rangePreset === "custom" && (
            <div className="flex items-center gap-1 text-xs">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="rounded-md border border-stone-200 px-2 py-1 text-xs"
                data-testid="range-start-input"
              />
              <span className="text-stone-400">→</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="rounded-md border border-stone-200 px-2 py-1 text-xs"
                data-testid="range-end-input"
              />
            </div>
          )}
          {rangePreset !== "custom" && startDate && endDate && (
            <span className="text-xs text-stone-500">
              {new Date(startDate).toLocaleDateString("nb-NO")} – {new Date(endDate).toLocaleDateString("nb-NO")}
            </span>
          )}
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="text-center py-12 text-stone-400">
          <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          Søker etter &quot;{q}&quot;…
        </div>
      ) : result ? (
        <div data-testid="usage-results">
          {/* Summary */}
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 rounded-xl p-4 mb-4" data-testid="usage-summary">
            <div className="flex items-baseline justify-between gap-2 mb-2">
              <div>
                <h2 className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Treff for «{result.query}»</h2>
                <div className="text-4xl font-bold text-emerald-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {result.matched_count}
                  <span className="text-base font-normal ml-1.5">kjøp</span>
                </div>
              </div>
              <div className="text-right">
                <span className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Total kostnad</span>
                <div className="text-2xl font-bold text-emerald-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {result.total_price_nok.toLocaleString("nb-NO")} <span className="text-sm font-normal">kr</span>
                </div>
              </div>
            </div>
            {result.per_unit.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2 border-t border-emerald-200">
                {result.per_unit.map((u) => (
                  <span key={u.unit} className="inline-flex items-baseline gap-1 bg-white border border-emerald-200 rounded-full px-3 py-1 text-sm" data-testid={`per-unit-${u.unit}`}>
                    <span className="font-bold text-emerald-900">{u.total.toLocaleString("nb-NO")}</span>
                    <span className="text-emerald-700">{u.unit}</span>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Trend-sammenligning mot forrige periode */}
          {result.trends && (
            <div className="bg-white border border-stone-200 rounded-xl p-4 mb-4" data-testid="trend-banner">
              <h3 className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold mb-2">
                Trend siste {result.trends.period_days} dager
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <TrendCard
                  label="Denne perioden"
                  count={result.matched_count}
                  price={result.total_price_nok}
                  range={`${new Date(result.start_date).toLocaleDateString("nb-NO")} – ${new Date(result.end_date).toLocaleDateString("nb-NO")}`}
                  highlight
                  testid="trend-current"
                />
                <TrendCard
                  label="Forrige periode"
                  count={result.trends.previous.matched_count}
                  price={result.trends.previous.total_price_nok}
                  range={`${new Date(result.trends.previous.start_date).toLocaleDateString("nb-NO")} – ${new Date(result.trends.previous.end_date).toLocaleDateString("nb-NO")}`}
                  testid="trend-previous"
                />
                <TrendCard
                  label="To tilbake"
                  count={result.trends.two_back.matched_count}
                  price={result.trends.two_back.total_price_nok}
                  range={`${new Date(result.trends.two_back.start_date).toLocaleDateString("nb-NO")} – ${new Date(result.trends.two_back.end_date).toLocaleDateString("nb-NO")}`}
                  testid="trend-two-back"
                />
              </div>
              {/* Pct change summary line */}
              {(result.trends.pct_change_vs_prev_count !== null || result.trends.pct_change_vs_prev_price !== null) && (
                <div className="mt-3 pt-3 border-t border-stone-100 text-sm" data-testid="trend-pct">
                  <PctChange
                    label={`kjøp av «${result.query}»`}
                    pct={result.trends.pct_change_vs_prev_count}
                    prevVal={result.trends.previous.matched_count}
                  />
                  {result.trends.pct_change_vs_prev_price !== null && (
                    <PctChange
                      label="kostnad"
                      pct={result.trends.pct_change_vs_prev_price}
                      prevVal={`${result.trends.previous.total_price_nok.toLocaleString("nb-NO")} kr`}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Per-receipt breakdown */}
          {result.occurrences.length === 0 ? (
            <div className="bg-white border border-stone-100 rounded-xl p-6 text-center text-sm text-stone-500" data-testid="no-occurrences">
              Ingen kvitteringar matcha søket i denne perioden.
              {rangePreset === "trip" && <><br/><button onClick={() => setRangePreset("all")} className="underline text-navy-800 mt-1">Søk i alle kvitteringar</button></>}
            </div>
          ) : (
            <div className="bg-white border border-stone-100 rounded-xl overflow-hidden" data-testid="occurrences-list">
              <h3 className="px-4 py-2 text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold border-b border-stone-100 flex items-center gap-1.5">
                <ShoppingCart className="h-3 w-3" /> Per kjøp
              </h3>
              <ul className="divide-y divide-stone-100">
                {result.occurrences.map((occ, i) => (
                  <li key={`${occ.receipt_id}-${i}`} className="px-4 py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`occurrence-${i}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-800 truncate">{occ.name}</p>
                      <p className="text-xs text-stone-400">
                        {occ.date && new Date(occ.date).toLocaleDateString("nb-NO")}
                        {occ.store_name && ` · ${occ.store_name}`}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-emerald-700">{occ.amount.toLocaleString("nb-NO")} {occ.unit}</p>
                      {occ.price > 0 && <p className="text-xs text-stone-500">{occ.price.toLocaleString("nb-NO")} kr</p>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-16 text-stone-400" data-testid="empty-state">
          <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Skriv eit vare-namn eller velg et forslag over for å sjå forbruket ditt.</p>
        </div>
      )}
    </div>
  );
}
